import { AppState } from 'store';
import { Section } from '../../../../server/graphql/__generated.types';
import { editorToPlainText } from '../Editor/utils/editorXml';

export function selectEditBuffer(state: AppState, id: string) {
  return state.scriptStudioBuffers.byId[id];
}

export function selectLastUsedAvatar(state: AppState) {
  return state.scriptStudio.lastUsedAvatar;
}

export interface SectionViewOptions {
  /**
   * `section-editor-sync`: prefer `section.editor` over the current take, so
   * sections created or edited outside Studio (MCP) show their latest script.
   */
  readSectionEditor?: boolean;
  /**
   * `section-editor-sync`: fall back to the model saved on the section when it
   * has no take, so a model picked before generating is shown.
   */
  readSectionModel?: boolean;
}

/**
 * The section's saved formatted text, before any local draft: with
 * `readSectionEditor`, the section's own `editor` when non-null (even empty:
 * the text was cleared after the take), otherwise the current take's.
 */
export function getSectionBaseFormattedText(
  base: Section | undefined,
  { readSectionEditor = false }: SectionViewOptions = {}
): string {
  if (readSectionEditor && base?.editor != null) return base.editor;
  return base?.currentTake?.formattedText ?? '';
}

/** Plain-text counterpart of `getSectionBaseFormattedText`. */
export function getSectionBaseText(
  base: Section | undefined,
  { readSectionEditor = false }: SectionViewOptions = {}
): string {
  if (readSectionEditor && base?.editor != null) {
    return editorToPlainText(base.editor);
  }
  return base?.currentTake?.text ?? '';
}

export function buildSectionView(
  base: Section | undefined,
  buffer?: {
    textDraft?: string;
    formattedTextDraft?: string;
    avatarDraft?: number;
    avatarVariantDraft?: number;
    avatarVariantVersionDraft?: string;
    lastEditedAt?: number;
  },
  defaultAvatarVariantVersion?: string,
  projectSampleRate: number = 24000,
  options: SectionViewOptions = {}
) {
  // The buffer is considered outdated if there are more recent takes
  // than the buffer was last edited. This indicates that a take has been
  // created since the buffer was last edited, and the buffer should be
  // ignored.
  const outdatedBuffer =
    buffer?.lastEditedAt && base?.currentTake?.createdAt
      ? buffer.lastEditedAt < Number(base.currentTake.createdAt)
      : false;

  const baseAvatarId =
    base?.currentTake?.avatarId ?? base?.defaultAvatarId ?? 4;
  const baseAvatarVariantId =
    base?.currentTake?.avatarVariantId ?? base?.defaultAvatarVariantId ?? 4;
  const baseTtsVersion = base?.currentTake?.ttsVersion;
  const baseText = getSectionBaseText(base, options);
  const baseFormattedText = getSectionBaseFormattedText(base, options);
  const text = outdatedBuffer ? baseText : buffer?.textDraft ?? baseText;
  const formattedText = outdatedBuffer
    ? baseFormattedText
    : buffer?.formattedTextDraft ?? baseFormattedText;
  const avatar = outdatedBuffer
    ? baseAvatarId
    : buffer?.avatarDraft ?? baseAvatarId ?? 1;
  const avatarVariant = outdatedBuffer
    ? baseAvatarVariantId
    : buffer?.avatarVariantDraft ?? baseAvatarVariantId ?? 1;

  const avatarVariantVersion =
    buffer?.avatarVariantVersionDraft ??
    baseTtsVersion ??
    (options.readSectionModel ? base?.defaultTtsVersion : undefined) ??
    defaultAvatarVariantVersion;
  const isTextDirty = outdatedBuffer
    ? false
    : buffer?.textDraft !== undefined &&
      buffer.textDraft !== (base?.currentTake?.text ?? '');
  const isFormattedTextDirty = outdatedBuffer
    ? false
    : buffer?.formattedTextDraft !== undefined &&
      buffer.formattedTextDraft !== (base?.currentTake?.formattedText ?? '');
  const isAvatarDirty = outdatedBuffer
    ? false
    : (buffer?.avatarDraft !== undefined &&
        buffer.avatarDraft !== (baseAvatarId ?? 4)) ||
      (buffer?.avatarVariantDraft !== undefined &&
        buffer.avatarVariantDraft !== (baseAvatarVariantId ?? 4)) ||
      // note: this will be undefined for script-first projects as we only render caruso
      (buffer?.avatarVariantVersionDraft !== undefined &&
        buffer.avatarVariantVersionDraft !== (baseTtsVersion ?? ''));

  // Sample rate is dirty if:
  // 1. Feature is enabled
  // 2. Project has a sample rate set
  // 3. Current take exists AND either:
  //    - Take has no sample rate (null/undefined) but project does, OR
  //    - Take's sample rate doesn't match project sample rate
  // Note: Standard/Legacy takes (v10/v11) are always forced to 24kHz regardless of project sample rate,
  // so we should not mark them as dirty based on sample rate. Only Caruso (v12) takes can have variable sample rates.
  // Note: We use Number() to ensure proper type coercion for comparison
  const takeSampleRate = base?.currentTake?.sampleRate;
  const takeSampleRateNum =
    takeSampleRate != null ? Number(takeSampleRate) : null;
  const projectSampleRateNum =
    projectSampleRate != null ? Number(projectSampleRate) : null;
  const ttsVersion =
    base?.currentTake?.ttsVersion ?? defaultAvatarVariantVersion ?? '';
  const isCaruso = ttsVersion.includes('v12');

  // Only check sample rate for Caruso takes. Standard/Legacy takes are always 24kHz.
  const isSampleRateDirty =
    !!projectSampleRateNum &&
    !!base?.currentTake &&
    isCaruso && // Only check sample rate for Caruso takes
    (takeSampleRateNum == null || takeSampleRateNum !== projectSampleRateNum);

  // With `readSectionEditor`, text saved on the section that the current take
  // doesn't have (never rendered, or edited by the MCP since) needs a render
  // like a draft does. Only when no draft is in effect: a draft is compared
  // against the take above.
  const hasDraft =
    !outdatedBuffer &&
    (buffer?.textDraft !== undefined ||
      buffer?.formattedTextDraft !== undefined);
  const isEditorAheadOfTake =
    !!options.readSectionEditor &&
    !hasDraft &&
    base?.editor != null &&
    base.editor !== base.currentTake?.formattedText;

  const isDirty =
    text.trim() !== '' &&
    (isEditorAheadOfTake ||
      isTextDirty ||
      isFormattedTextDirty ||
      isAvatarDirty ||
      isSampleRateDirty);

  return {
    base,
    text,
    avatar,
    avatarVariant,
    avatarVariantVersion,
    isDirty,
    formattedText,
    sampleRate: projectSampleRate,
  };
}

export function isBulkGenerateUiActive(
  isBulkQueued: boolean,
  hasCurrentTake: boolean,
  isDirty: boolean
): boolean {
  return isBulkQueued && !(hasCurrentTake && !isDirty);
}
